import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import "./tableeOrders.css";
import "../commerce/commerceAdmin.css";
import "../commerce/productEditor.css";

const emptyProduct = {
  sku: "",
  name: "",
  category: "",
  subcategory: "General",
  unitLabel: "",
  price: "",
  priceRanges: [{ minQty: "1", maxQty: "", previousPrice: "", discountPercent: "", price: "" }],
  stockQty: "",
  imageUrl: "",
  imageUrls: [],
  description: "",
  productOptions: [],
  suggestions: [],
  isActive: true,
  isFeatured: false,
  featuredRank: "",
  olivaHomeFeatured: false,
  olivaHomeRank: "",
  storefronts: [],
  isRental: false,
  rentalDepositAmount: "",
  rentalTerms: "",
  trackStock: false,
};

const suggestionTypeOptions = [
  { value: "complement", label: "Complemento" },
  { value: "upgrade", label: "Upgrade" },
  { value: "substitute", label: "Sustituto" },
  { value: "bundle", label: "Bundle" },
  { value: "frequently_bought", label: "Frecuente" },
];

const emptyCategoryForm = {
  category: "",
  subcategory: "General",
};

function calculateDiscountedPrice(previousPrice, discountPercent) {
  const previous = Number(previousPrice);
  const discount = discountPercent === "" || discountPercent == null ? 0 : Number(discountPercent);
  if (!Number.isFinite(previous) || previous < 0 || !Number.isFinite(discount) || discount < 0) return "";
  return String(Math.round(previous * (1 - Math.min(discount, 100) / 100)));
}

function normalizeDiscountPercent(value) {
  if (value === "" || value == null) return "";
  const discount = Number(value);
  if (!Number.isFinite(discount)) return "";
  return String(Math.min(100, Math.max(0, discount)));
}

function normalizePriceRangeLadder(ranges, { preserveIncomplete = true } = {}) {
  const source = ranges?.length ? ranges : [{ minQty: "1", maxQty: "", previousPrice: "", discountPercent: "", price: "" }];
  let nextMin = 1;

  return source.map((range, index) => {
    const isLast = index === source.length - 1;
    const hasComputedMin = Number.isFinite(nextMin);
    const minQty = hasComputedMin ? String(nextMin) : "";
    let maxQty = isLast ? "" : range.maxQty;

    if (!isLast) {
      const parsedMax = Number(maxQty);
      if (hasComputedMin && Number.isFinite(parsedMax) && parsedMax >= nextMin) {
        maxQty = String(parsedMax);
        nextMin = parsedMax + 1;
      } else if (preserveIncomplete) {
        maxQty = maxQty == null ? "" : String(maxQty);
        nextMin = null;
      } else {
        maxQty = hasComputedMin ? String(nextMin) : "";
        nextMin = hasComputedMin ? nextMin + 1 : null;
      }
    }

    return {
      ...range,
      minQty,
      maxQty,
      previousPrice: range.previousPrice ?? range.price ?? "",
      discountPercent: range.discountPercent ?? "",
      price: calculateDiscountedPrice(range.previousPrice ?? range.price ?? "", range.discountPercent ?? ""),
    };
  });
}

function preparePriceRangeLadderForAppend(ranges) {
  const normalized = normalizePriceRangeLadder(ranges);
  const lastIndex = normalized.length - 1;
    const lastRange = normalized[lastIndex] || { minQty: "1", maxQty: "", previousPrice: "", discountPercent: "", price: "" };
  const parsedMin = Number(lastRange.minQty);
  const nextMax = Number.isFinite(parsedMin) ? String(parsedMin) : "";

  return normalized.map((range, index) =>
    index === lastIndex ? { ...range, maxQty: nextMax } : range
  );
}

function CustomSelect({ value, options, placeholder, disabled, isOpen, onOpen, onClose, onChange }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);
  const selectedOption = options.find((option) => String(option.value) === String(value));
  const selectedLabel = selectedOption?.label || value;

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    function updateMenuRect() {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateMenuRect();
    window.addEventListener("resize", updateMenuRect);
    window.addEventListener("scroll", updateMenuRect, true);
    return () => {
      window.removeEventListener("resize", updateMenuRect);
      window.removeEventListener("scroll", updateMenuRect, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleOutsidePointer(event) {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      onClose();
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("mousedown", handleOutsidePointer, true);
    document.addEventListener("touchstart", handleOutsidePointer, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsidePointer, true);
      document.removeEventListener("touchstart", handleOutsidePointer, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const menu = isOpen && menuRect ? (
    <div
      ref={menuRef}
      className="tablee-tool__custom-select-menu tablee-tool__custom-select-menu--floating"
      role="listbox"
      style={{ left: menuRect.left, top: menuRect.top, width: menuRect.width }}
    >
      {options.length ? (
        options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={String(option.value) === String(value)}
            className={String(option.value) === String(value) ? "is-selected" : ""}
            onClick={() => {
              onChange(option.value);
              onClose();
            }}
          >
            {option.label}
          </button>
        ))
      ) : (
        <span className="tablee-tool__custom-select-empty">Sin opciones</span>
      )}
    </div>
  ) : null;

  return (
    <div className={`tablee-tool__custom-select${disabled ? " is-disabled" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="tablee-tool__custom-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? onClose() : onOpen())}
      >
        <span className={selectedLabel ? "" : "is-placeholder"}>{selectedLabel || placeholder}</span>
        <span className="material-symbols-outlined">expand_more</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

function ProductSuggestionSelect({
  value,
  products,
  query,
  placeholder,
  disabled,
  isOpen,
  onOpen,
  onClose,
  onQueryChange,
  onChange,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);
  const selectedProduct = products.find((product) => String(product.id) === String(value));

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    function updateMenuRect() {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateMenuRect();
    window.addEventListener("resize", updateMenuRect);
    window.addEventListener("scroll", updateMenuRect, true);
    return () => {
      window.removeEventListener("resize", updateMenuRect);
      window.removeEventListener("scroll", updateMenuRect, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleOutsidePointer(event) {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      onClose();
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("mousedown", handleOutsidePointer, true);
    document.addEventListener("touchstart", handleOutsidePointer, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsidePointer, true);
      document.removeEventListener("touchstart", handleOutsidePointer, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const filteredProducts = products.filter((product) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return true;
    return `${product.name || ""} ${product.sku || ""} ${product.category || ""}`.toLowerCase().includes(normalizedQuery);
  }).slice(0, 40);

  const menu = isOpen && menuRect ? (
    <div
      ref={menuRef}
      className="tablee-tool__custom-select-menu tablee-tool__custom-select-menu--floating tablee-tool__product-picker-menu"
      role="listbox"
      style={{ left: menuRect.left, top: menuRect.top, width: menuRect.width }}
    >
      <label className="tablee-tool__product-picker-search">
        <span className="material-symbols-outlined">search</span>
        <input
          ref={searchRef}
          value={query}
          placeholder="Buscar producto"
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </label>
      <div className="tablee-tool__product-picker-options">
        {filteredProducts.length ? (
          filteredProducts.map((product) => (
            <button
              key={product.id}
              type="button"
              role="option"
              aria-selected={String(product.id) === String(value)}
              className={String(product.id) === String(value) ? "is-selected" : ""}
              onClick={() => {
                onChange(product.id);
                onClose();
              }}
            >
              <span>{product.name}</span>
              <small>{[product.sku, product.category].filter(Boolean).join(" · ")}</small>
            </button>
          ))
        ) : (
          <span className="tablee-tool__custom-select-empty">Sin productos disponibles</span>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`tablee-tool__custom-select${disabled ? " is-disabled" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="tablee-tool__custom-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? onClose() : onOpen())}
      >
        <span className={selectedProduct ? "" : "is-placeholder"}>{selectedProduct?.name || placeholder}</span>
        <span className="material-symbols-outlined">expand_more</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function TableeOrders({ embedded = false, active = true, onManageInventory, headerActionsTarget } = {}) {
  const Heading = embedded ? "h2" : "h1";
  const [products, setProducts] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [productFormSection, setProductFormSection] = useState("main");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState("add");
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [subcategoryDrafts, setSubcategoryDrafts] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [addingSubcategoryFor, setAddingSubcategoryFor] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [creatingCategoryEntry, setCreatingCategoryEntry] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [optionsHelpPosition, setOptionsHelpPosition] = useState(null);
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [selectedSuggestionProductId, setSelectedSuggestionProductId] = useState("");

  async function loadProducts() {
    const response = await fetch(apiUrl("/commerce/products"), {
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (data.success) {
      setProducts(data.products || []);
    } else {
      setMessage(data.message || "No se pudieron cargar los productos.");
    }
  }

  async function loadCategories() {
    const response = await fetch(apiUrl("/commerce/categories"), {
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (data.success) {
      setCategories(data.categories || []);
    } else {
      setMessage(data.message || "No se pudieron cargar las categorias.");
    }
  }

  useEffect(() => {
    if (!active) return;
    loadProducts();
    loadCategories();
  }, [active]);

  useEffect(() => {
    if (!active || !showForm) return undefined;

    function handleEscape(event) {
      if (event.key === "Escape") {
        if (openDropdown) {
          setOpenDropdown(null);
          return;
        }
        setForm(emptyProduct);
        setEditingId(null);
        setShowForm(false);
        setProductFormSection("main");
        setSuggestionQuery("");
        setSelectedSuggestionProductId("");
        setShowCategoryModal(false);
        setCategoryForm(emptyCategoryForm);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [active, showForm, openDropdown]);

  useEffect(() => {
    setOpenDropdown(null);
  }, [productFormSection, showForm]);

  useEffect(() => {
    if (!optionsHelpPosition) return undefined;

    function closeOptionsHelp() {
      setOptionsHelpPosition(null);
    }

    window.addEventListener("scroll", closeOptionsHelp, true);
    window.addEventListener("resize", closeOptionsHelp);
    return () => {
      window.removeEventListener("scroll", closeOptionsHelp, true);
      window.removeEventListener("resize", closeOptionsHelp);
    };
  }, [optionsHelpPosition]);

  useEffect(() => {
    if (!openDropdown) return undefined;

    function handleOutsideClick(event) {
      if (
        !event.target.closest(".tablee-tool__custom-select") &&
        !event.target.closest(".tablee-tool__custom-select-menu")
      ) {
        setOpenDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openDropdown]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function uploadProductImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("Selecciona un archivo de imagen.");
      return;
    }

    if (file.size > 6 * 1024 * 1024) {
      setMessage("La imagen debe pesar menos de 6 MB.");
      return;
    }

    setUploadingImage(true);
    setMessage("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const response = await fetch(apiUrl("/commerce/product-images"), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type,
          dataUrl,
        }),
      });
      const data = await response.json();

      if (!data.success) {
        setMessage(data.message || "No se pudo subir la imagen.");
        return;
      }

      const imageUrl = data.imageUrl || "";
      if (imageUrl) {
        setForm((current) => {
          const imageUrls = [...new Set([...(current.imageUrls || []), imageUrl])];
          return { ...current, imageUrl: imageUrls[0] || "", imageUrls };
        });
      }
      setMessage("Imagen subida.");
    } catch (_error) {
      setMessage("No se pudo subir la imagen.");
    } finally {
      setUploadingImage(false);
    }
  }

  function addPriceRange() {
    setForm((current) => ({
      ...current,
      priceRanges: normalizePriceRangeLadder([
        ...preparePriceRangeLadderForAppend(current.priceRanges || []),
        { minQty: "", maxQty: "", previousPrice: "", discountPercent: "", price: "" },
      ]),
    }));
  }

  function updatePriceRange(index, field, value) {
    setForm((current) => ({
      ...current,
      priceRanges: normalizePriceRangeLadder(
        (current.priceRanges || []).map((range, rangeIndex) =>
          rangeIndex === index ? { ...range, [field]: value } : range
        )
      ),
    }));
  }

  function removePriceRange(index) {
    setForm((current) => ({
      ...current,
      priceRanges: normalizePriceRangeLadder(
        (current.priceRanges || []).filter((_range, rangeIndex) => rangeIndex !== index)
      ),
    }));
  }

  function confirmRemovePriceRange(index) {
    setConfirmDialog({
      title: "Eliminar rango",
      message: `Eliminar el rango de precio ${index + 1}?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        removePriceRange(index);
        setConfirmDialog(null);
      },
    });
  }

  function openCategoryModal(mode) {
    setOpenDropdown(null);
    setCategoryModalMode("add");
    setCategoryForm({
      category: mode === "subcategory" ? form.category : "",
      subcategory: mode === "subcategory" ? "" : "General",
    });
    setShowCategoryModal(true);
  }

  function openCategoryManager() {
    setOpenDropdown(null);
    setCategoryModalMode("manage");
    setCategoryForm(emptyCategoryForm);
    setShowCategoryModal(true);
    setMessage("");
  }

  function closeCategoryModal() {
    setShowCategoryModal(false);
    setCategoryForm(emptyCategoryForm);
    setSubcategoryDrafts({});
    setAddingSubcategoryFor({});
    setEditingCategory(null);
    setEditingSubcategory(null);
  }

  function toggleCategoryCollapsed(categoryName) {
    setCollapsedCategories((current) => ({
      ...current,
      [categoryName]: !current[categoryName],
    }));
  }

  async function handleCategoryPlusClick(categoryName) {
    if (addingSubcategoryFor[categoryName] && subcategoryDrafts[categoryName]?.trim()) {
      await addSubcategoryToCategory(categoryName);
      return;
    }

    setCollapsedCategories((current) => ({ ...current, [categoryName]: false }));
    setAddingSubcategoryFor((current) => ({
      ...current,
      [categoryName]: !current[categoryName],
    }));
  }

  async function createCategoryOption(category, subcategory, { closeAfter = false, selectAfter = true } = {}) {
    const normalizedCategory = category.trim();
    const normalizedSubcategory = subcategory.trim() || "General";

    if (!normalizedCategory) {
      setMessage("Ingresa una categoria para agregarla.");
      return false;
    }

    setMessage("");
    let data;
    try {
      const response = await fetch(apiUrl("/commerce/categories"), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({ category: normalizedCategory, subcategory: normalizedSubcategory }),
      });
      data = await response.json();
    } catch (_error) {
      setMessage("No se pudo conectar para agregar la categoria.");
      return false;
    }

    if (!data?.success) {
      setMessage(data?.message || "No se pudo agregar la categoria.");
      return false;
    }

    if (selectAfter) {
      setForm((current) => ({
        ...current,
        category: data.category?.category || normalizedCategory,
        subcategory: data.category?.subcategory || normalizedSubcategory,
      }));
    }
    if (closeAfter) {
      closeCategoryModal();
    }
    await loadCategories();
    return true;
  }

  async function saveCategoryOption() {
    const category = categoryForm.category.trim();
    const subcategory = categoryForm.subcategory.trim() || "General";
    const saved = await createCategoryOption(category, subcategory, {
      closeAfter: categoryModalMode !== "manage",
      selectAfter: categoryModalMode !== "manage",
    });
    if (saved && categoryModalMode === "manage") {
      setCategoryForm(emptyCategoryForm);
    }
  }

  async function createEditableCategoryEntry() {
    if (creatingCategoryEntry) return;

    const existingCategories = new Set(categoryOptions.map((category) => category.toLowerCase()));
    let categoryName = "Nueva categoria";
    let suffix = 2;

    while (existingCategories.has(categoryName.toLowerCase())) {
      categoryName = `Nueva categoria ${suffix}`;
      suffix += 1;
    }

    setCreatingCategoryEntry(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/commerce/categories"), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({ category: categoryName, subcategory: "General" }),
      });
      const data = await response.json();

      if (!data?.success) {
        setMessage(data?.message || "No se pudo agregar la categoria.");
        return;
      }

      const newCategory = data.category || { category: categoryName, subcategory: "General" };

      setCategories((current) => [
        newCategory,
        ...current.filter((category) => category.id !== newCategory.id),
      ]);
      setCollapsedCategories((current) => ({ ...current, [newCategory.category]: false }));
      setEditingSubcategory(null);
      setEditingCategory({ category: newCategory.category, value: newCategory.category });
    } catch (_error) {
      setMessage("No se pudo conectar para agregar la categoria.");
    } finally {
      setCreatingCategoryEntry(false);
    }
  }

  async function saveCategoryManagerChanges() {
    if (editingCategory) {
      await saveCategoryGroupEdit();
      return;
    }

    if (editingSubcategory) {
      await saveSubcategoryOptionEdit();
      return;
    }

    const categoryWithDraft = Object.keys(addingSubcategoryFor).find(
      (categoryName) => addingSubcategoryFor[categoryName] && subcategoryDrafts[categoryName]?.trim()
    );

    if (categoryWithDraft) {
      await addSubcategoryToCategory(categoryWithDraft);
      return;
    }

    closeCategoryModal();
  }

  async function addSubcategoryToCategory(categoryName) {
    const draft = subcategoryDrafts[categoryName]?.trim() || "";
    if (!draft) {
      setMessage("Ingresa una subcategoria para agregarla.");
      return;
    }

    const saved = await createCategoryOption(categoryName, draft, {
      closeAfter: false,
      selectAfter: false,
    });
    if (saved) {
      setSubcategoryDrafts((current) => ({ ...current, [categoryName]: "" }));
      setAddingSubcategoryFor((current) => ({ ...current, [categoryName]: false }));
    }
  }

  function deleteCategoryOption(category) {
    const label = `${category.category}${category.subcategory ? ` / ${category.subcategory}` : ""}`;
    setConfirmDialog({
      title: "Eliminar subcategoria",
      message: `Eliminar "${label}"? Solo se puede eliminar si no esta en uso.`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteCategoryOption(category),
    });
  }

  async function confirmDeleteCategoryOption(category) {
    setConfirmDialog(null);
    setMessage("");
    const response = await fetch(apiUrl(`/commerce/categories/${category.id}`), {
      method: "DELETE",
      headers: getSessionHeaders(),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar la categoria.");
      return;
    }

    await loadCategories();
  }

  function startEditCategoryGroup(categoryName) {
    setEditingSubcategory(null);
    setEditingCategory({ category: categoryName, value: categoryName });
  }

  async function saveCategoryGroupEdit() {
    if (!editingCategory) return;

    const categoryName = editingCategory.category;
    const normalizedCategory = editingCategory.value.trim();
    if (!normalizedCategory) {
      setMessage("Ingresa un nombre de categoria valido.");
      return;
    }
    if (normalizedCategory === categoryName) {
      setEditingCategory(null);
      return;
    }

    setMessage("");
    const response = await fetch(apiUrl("/commerce/categories/group"), {
      method: "PUT",
      headers: getSessionHeaders(),
      body: JSON.stringify({ currentCategory: categoryName, category: normalizedCategory }),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo editar la categoria.");
      return;
    }

    setCollapsedCategories((current) => {
      const next = { ...current };
      next[normalizedCategory] = next[categoryName];
      delete next[categoryName];
      return next;
    });
    setAddingSubcategoryFor((current) => {
      const next = { ...current };
      next[normalizedCategory] = next[categoryName];
      delete next[categoryName];
      return next;
    });
    setSubcategoryDrafts((current) => {
      const next = { ...current };
      next[normalizedCategory] = next[categoryName] || "";
      delete next[categoryName];
      return next;
    });
    await loadCategories();
    await loadProducts();
    setEditingCategory(null);
  }

  function deleteCategoryGroup(categoryName) {
    setConfirmDialog({
      title: "Eliminar categoria",
      message: `Eliminar la categoria "${categoryName}" y sus subcategorias? Solo se puede eliminar si no esta en uso.`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteCategoryGroup(categoryName),
    });
  }

  async function confirmDeleteCategoryGroup(categoryName) {
    setConfirmDialog(null);
    setMessage("");
    const response = await fetch(apiUrl(`/commerce/categories/group/${encodeURIComponent(categoryName)}`), {
      method: "DELETE",
      headers: getSessionHeaders(),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar la categoria.");
      return;
    }

    await loadCategories();
  }

  function startEditSubcategoryOption(category) {
    setEditingCategory(null);
    setEditingSubcategory({
      id: category.id,
      category: category.category,
      subcategory: category.subcategory || "General",
      value: category.subcategory || "General",
    });
  }

  async function saveSubcategoryOptionEdit() {
    if (!editingSubcategory) return;

    const normalizedSubcategory = editingSubcategory.value.trim() || "General";
    if (normalizedSubcategory === editingSubcategory.subcategory) {
      setEditingSubcategory(null);
      return;
    }

    setMessage("");
    const response = await fetch(apiUrl(`/commerce/categories/${editingSubcategory.id}`), {
      method: "PUT",
      headers: getSessionHeaders(),
      body: JSON.stringify({ subcategory: normalizedSubcategory }),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo editar la subcategoria.");
      return;
    }

    await loadCategories();
    await loadProducts();
    setEditingSubcategory(null);
  }

  function editProduct(product) {
    setEditingId(product.id);
    setShowForm(true);
    setProductFormSection("main");
    setForm({
      sku: product.sku || "",
      name: product.name || "",
      category: product.category || "",
      subcategory: product.subcategory || "General",
      unitLabel: product.unitLabel || "",
      price: product.price || "",
      priceRanges: normalizePriceRangeLadder(product.priceRanges?.length
        ? product.priceRanges
        : [{ minQty: "1", maxQty: "", previousPrice: product.price || "", discountPercent: "", price: product.price || "" }]),
      stockQty: product.stockQty || "",
      imageUrl: product.imageUrl || "",
      imageUrls: product.imageUrls?.length ? product.imageUrls : (product.imageUrl ? [product.imageUrl] : []),
      description: product.description || "",
      productOptions: Array.isArray(product.productOptions) ? product.productOptions : [],
      isActive: product.isActive !== false,
      isFeatured: Boolean(product.isFeatured),
      featuredRank: product.featuredRank ?? "",
      olivaHomeFeatured: Boolean(product.olivaHomeFeatured),
      olivaHomeRank: product.olivaHomeRank ?? "",
      suggestions: Array.isArray(product.suggestions) ? product.suggestions : [],
      storefronts: Array.isArray(product.storefronts) ? product.storefronts : ["tablee"],
      isRental: Boolean(product.isRental),
      rentalDepositAmount: product.rentalDepositAmount ?? "",
      rentalTerms: product.rentalTerms || "",
      trackStock: Boolean(product.trackStock),
    });
    setSuggestionQuery("");
    setSelectedSuggestionProductId("");
  }

  async function saveProduct(event) {
    event.preventDefault();
    if (uploadingImage) {
      setMessage("Espera a que termine de subir la imagen antes de guardar.");
      return;
    }
    setMessage("");
    const olivaHomeRank = form.olivaHomeFeatured && form.olivaHomeRank !== ""
      ? Number(form.olivaHomeRank)
      : null;
    if (olivaHomeRank !== null && (!Number.isSafeInteger(olivaHomeRank) || olivaHomeRank < 0)) {
      setMessage("El orden en inicio de Oliva debe ser un número entero mayor o igual a 0.");
      return;
    }

    const tableeFeaturedRank = form.featuredRank === "" ? null : Number(form.featuredRank);
    if (tableeFeaturedRank !== null && (!Number.isSafeInteger(tableeFeaturedRank) || tableeFeaturedRank < 0)) {
      setMessage("El orden en Tablee debe ser un número entero mayor o igual a 0.");
      return;
    }

    const payload = {
      ...form,
      olivaHomeFeatured: Boolean(form.olivaHomeFeatured),
      olivaHomeRank,
      priceRanges: normalizePriceRangeLadder(form.priceRanges || []).map((range) => ({
        minQty: range.minQty === "" ? "" : Number(range.minQty),
        maxQty: range.maxQty === "" || range.maxQty == null ? null : Number(range.maxQty),
        previousPrice: range.previousPrice === "" ? "" : Number(range.previousPrice),
        discountPercent: range.discountPercent === "" ? 0 : Number(range.discountPercent),
        price: calculateDiscountedPrice(range.previousPrice, range.discountPercent) === ""
          ? ""
          : Number(calculateDiscountedPrice(range.previousPrice, range.discountPercent)),
      })),
      stockQty: Number(form.stockQty || 0),
      storefronts: form.storefronts || [],
      isRental: Boolean(form.isRental),
      rentalDepositAmount: form.isRental ? Number(form.rentalDepositAmount || 0) : 0,
      rentalTerms: form.isRental ? String(form.rentalTerms || "").trim() : "",
      trackStock: !form.isRental && Boolean(form.trackStock),
      imageUrl: (form.imageUrls || [])[0] || form.imageUrl || "",
      imageUrls: form.imageUrls || [],
      productOptions: (form.productOptions || [])
        .map((option) => ({
          name: String(option.name || "").trim(),
          values: (Array.isArray(option.values) ? option.values : [])
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        }))
        .filter((option) => option.name && option.values.length),
      suggestions: (form.suggestions || [])
        .map((suggestion, index) => ({
          suggestedProductId: Number(suggestion.suggestedProductId),
          type: suggestion.type || "complement",
          priority: Number(suggestion.priority || index + 1),
          isActive: suggestion.isActive !== false,
        }))
        .filter((suggestion) => Number.isInteger(suggestion.suggestedProductId)),
      featuredRank: tableeFeaturedRank,
    };

    const response = await fetch(apiUrl(editingId ? `/commerce/products/${editingId}` : "/commerce/products"), {
      method: editingId ? "PATCH" : "POST",
      headers: getSessionHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo guardar el producto.");
      return;
    }

    setForm(emptyProduct);
    setEditingId(null);
    setShowForm(false);
    setMessage("Producto guardado.");
    await loadProducts();
  }

  function removeProductImage(imageUrl) {
    setForm((current) => {
      const imageUrls = (current.imageUrls || []).filter((item) => item !== imageUrl);
      return {
        ...current,
        imageUrl: imageUrls[0] || "",
        imageUrls,
      };
    });
  }

  function addDescriptionBullet() {
    setForm((current) => {
      const description = current.description || "";
      const prefix = description && !description.endsWith("\n") ? "\n" : "";
      return { ...current, description: `${description}${prefix}• ` };
    });
  }

  function addProductOption() {
    setForm((current) => ({
      ...current,
      productOptions: [...(current.productOptions || []), { name: "", values: [] }],
    }));
  }

  function updateProductOption(index, field, value) {
    setForm((current) => ({
      ...current,
      productOptions: (current.productOptions || []).map((option, optionIndex) => {
        if (optionIndex !== index) return option;
        if (field === "values") {
          return {
            ...option,
            values: value.split(",").map((item) => item.trim()).filter(Boolean),
          };
        }
        return { ...option, [field]: value };
      }),
    }));
  }

  function removeProductOption(index) {
    setForm((current) => ({
      ...current,
      productOptions: (current.productOptions || []).filter((_option, optionIndex) => optionIndex !== index),
    }));
  }

  function addProductSuggestion() {
    const suggestedProductId = Number(selectedSuggestionProductId);
    if (!Number.isInteger(suggestedProductId)) {
      setMessage("Selecciona un producto para sugerir.");
      return;
    }
    if (editingId && Number(editingId) === suggestedProductId) {
      setMessage("Un producto no se puede sugerir a si mismo.");
      return;
    }
    if ((form.suggestions || []).some((suggestion) => Number(suggestion.suggestedProductId) === suggestedProductId)) {
      setMessage("Ese producto ya esta en la lista de sugerencias.");
      return;
    }

    setForm((current) => ({
      ...current,
      suggestions: [
        ...(current.suggestions || []),
        {
          suggestedProductId,
          type: "complement",
          priority: (current.suggestions || []).length + 1,
          isActive: true,
        },
      ],
    }));
    setSelectedSuggestionProductId("");
    setSuggestionQuery("");
    setMessage("");
  }

  function updateProductSuggestion(index, field, value) {
    setForm((current) => ({
      ...current,
      suggestions: (current.suggestions || []).map((suggestion, suggestionIndex) =>
        suggestionIndex === index ? { ...suggestion, [field]: value } : suggestion
      ),
    }));
  }

  function removeProductSuggestion(index) {
    setForm((current) => ({
      ...current,
      suggestions: (current.suggestions || []).filter((_suggestion, suggestionIndex) => suggestionIndex !== index),
    }));
  }

  function deleteProduct(product) {
    setConfirmDialog({
      title: "Eliminar producto",
      message: `Eliminar "${product.name}" del catálogo compartido?`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteProduct(product),
    });
  }

  async function confirmDeleteProduct(product) {
    setConfirmDialog(null);
    setMessage("");
    const response = await fetch(apiUrl(`/commerce/products/${product.id}`), {
      method: "DELETE",
      headers: getSessionHeaders(),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar el producto.");
      return;
    }

    setMessage("Producto eliminado.");
    await loadProducts();
  }

  async function toggleProductActive(product) {
    setProducts((current) =>
      current.map((item) => item.id === product.id ? { ...item, isActive: !product.isActive } : item)
    );

    const response = await fetch(apiUrl(`/commerce/products/${product.id}/active`), {
      method: "PATCH",
      headers: getSessionHeaders(),
      body: JSON.stringify({ isActive: !product.isActive }),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo actualizar el estado.");
      await loadProducts();
    }
  }

  function startNewProduct() {
    setForm(emptyProduct);
    setEditingId(null);
    setShowForm(true);
    setProductFormSection("main");
    setSuggestionQuery("");
    setSelectedSuggestionProductId("");
    setMessage("");
  }

  function cancelForm() {
    setForm(emptyProduct);
    setEditingId(null);
    setShowForm(false);
    setProductFormSection("main");
    setOpenDropdown(null);
    setSuggestionQuery("");
    setSelectedSuggestionProductId("");
    closeCategoryModal();
  }

  const visibleProducts = products.filter((product) => {
    const storefronts = Array.isArray(product.storefronts) ? product.storefronts : ["tablee"];
    if (["tablee", "oliva"].includes(catalogFilter) && !storefronts.includes(catalogFilter)) return false;
    if (catalogFilter === "rental" && !product.isRental) return false;
    if (catalogFilter === "sale" && product.isRental) return false;
    const text = `${product.name || ""} ${product.sku || ""} ${product.category || ""}`.toLocaleLowerCase("es-CL");
    return text.includes(search.trim().toLocaleLowerCase("es-CL"));
  });

  const categoryOptions = Array.from(new Set(categories.map((item) => item.category))).filter(Boolean);
  const subcategoryOptions = categories
    .filter((item) => item.category === form.category)
    .map((item) => item.subcategory)
    .filter(Boolean);
  const categoryTree = categoryOptions.map((category) => ({
    category,
    options: categories.filter((item) => item.category === category),
  }));
  const categorySelectOptions = categoryOptions.map((category) => ({ value: category, label: category }));
  const subcategorySelectOptions = subcategoryOptions.map((subcategory) => ({ value: subcategory, label: subcategory }));
  const selectedSuggestionIds = new Set((form.suggestions || []).map((suggestion) => String(suggestion.suggestedProductId)));
  const suggestionProductOptions = products.filter((product) =>
    String(product.id) !== String(editingId || "") && !selectedSuggestionIds.has(String(product.id))
  );
  const productById = products.reduce((acc, product) => {
    acc[String(product.id)] = product;
    return acc;
  }, {});

  const headerActions = (
<div className="tablee-tool__header-actions">
          <button type="button" className="tablee-tool__secondary" onClick={openCategoryManager}>
            Categorias
          </button>
          <button type="button" onClick={startNewProduct}>Agregar producto</button>
        </div>
  );

  return (
    <div className="tablee-tool">
      {embedded && headerActionsTarget ? createPortal(headerActions, headerActionsTarget) : null}
      {!embedded ? <header className="tablee-tool__header">
        <span className="commerce-catalog__icon material-symbols-outlined" aria-hidden="true">inventory_2</span>
        <div>
          <span>Comercio · Catálogo compartido</span>
          <Heading>{embedded ? "Catálogo y publicación" : "Productos y arriendos"}</Heading>
          <p>Un catálogo para Tablee y Oliva y Miel. Elige en qué tiendas se publica cada producto.</p>
        </div>
        {headerActions}
      </header> : null}

      <section className="tablee-tool__list">
        <div className="tablee-tool__list-head">
          <div>
            <span>Venta y arriendo</span>
            <h2>Catálogo de productos</h2>
          </div>
          <strong>{visibleProducts.length} de {products.length} productos</strong>
        </div>
        <div className="commerce-catalog__filters">
          <label className="commerce-catalog__search"><span className="material-symbols-outlined" aria-hidden="true">search</span><input aria-label="Buscar productos" placeholder="Buscar por nombre, SKU o categoría" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          <div className="commerce-catalog__filter-options" role="group" aria-label="Filtrar catálogo">
            {[["all", "Todos"], ["tablee", "Tablee"], ["oliva", "Oliva y Miel"], ["sale", "Venta"], ["rental", "Arriendos"]].map(([value, label]) => <button key={value} type="button" className={catalogFilter === value ? "is-selected" : ""} aria-pressed={catalogFilter === value} onClick={() => setCatalogFilter(value)}>{label}</button>)}
          </div>
        </div>
        <div className="tablee-tool__table-header" aria-hidden="true">
          <span>Imagen</span>
          <span>Producto</span>
          <span className="commerce-catalog__store-heading">Tienda</span>
          <span className="commerce-catalog__type-heading">Tipo</span>
          <span>Precio</span>
          <span>Stock</span>
          <span>Estado</span>
          <span>Destacados</span>
          <span>Acciones</span>
        </div>
        <div className="tablee-tool__products">
          {visibleProducts.map((product) => (
            <article key={product.id} className={product.isFeatured ? "is-featured" : ""}>
              <img src={product.imageUrl || "/images/tablee_logo.png"} alt="" />
              <div className="tablee-tool__product-main">
                <span>{product.category}{product.subcategory ? ` / ${product.subcategory}` : ""}</span>
                <h2>{product.name}</h2>
                <p>{product.sku}</p>
              </div>
              <div className="commerce-catalog__stores-cell" aria-label="Tienda">
                {(Array.isArray(product.storefronts) ? product.storefronts : ["tablee"]).map((store) => <span key={store}>{store === "oliva" ? "Oliva y Miel" : "Tablee"}</span>)}
                {Array.isArray(product.storefronts) && !product.storefronts.length ? <span>Sin tienda seleccionada</span> : null}
              </div>
              <div className="commerce-catalog__type-cell" aria-label="Tipo"><span className={product.isRental ? "is-rental" : ""}>{product.isRental ? "Arriendo / día" : "Venta"}</span></div>
              <strong className="commerce-catalog__price">${Number(product.price).toLocaleString("es-CL")}</strong>
              <small className="commerce-catalog__stock">{product.stockQty} {product.unitLabel}</small>
              <label className="tablee-tool__switch commerce-catalog__status">
                <input type="checkbox" checked={product.isActive} onChange={() => toggleProductActive(product)} />
                <span />
                <strong>{product.isActive ? "Activo" : "Oculto"}</strong>
              </label>
              <div className="commerce-catalog__featured-status">
                <small>{product.isFeatured ? `Tablee #${product.featuredRank ?? "—"}` : "Tablee: sin destacar"}</small>
                <small className={product.olivaHomeFeatured ? "is-oliva-featured" : ""}>{product.olivaHomeFeatured ? `Inicio Oliva #${product.olivaHomeRank ?? "—"}` : "Oliva: sin destacar"}</small>
              </div>
              <div className="tablee-tool__row-actions">
                {onManageInventory ? <button type="button" onClick={() => onManageInventory(product)} aria-label={`Inventario de ${product.name}`} title="Stock, costos, etiquetas e historial">
                  <span className="material-symbols-outlined">inventory_2</span>
                </button> : null}
                <button type="button" onClick={() => editProduct(product)} aria-label={`Editar ${product.name}`}>
                  <span className="material-symbols-outlined">edit</span>
                </button>
                <button type="button" className="tablee-tool__danger" onClick={() => deleteProduct(product)} aria-label={`Eliminar ${product.name}`}>
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </article>
          ))}
          {!visibleProducts.length ? <p className="commerce-catalog__empty" role="status">{message || (products.length ? "No hay productos para este filtro." : "Aún no hay productos. Agrega el primero para comenzar.")}</p> : null}
        </div>
      </section>

      {showForm ? (
        <div className="tablee-tool__modal-backdrop" role="presentation" onMouseDown={cancelForm}>
          <form
            className="tablee-tool__modal tablee-tool__modal--product"
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-product-editor-title"
            onSubmit={saveProduct}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-tool__modal-head">
              <div>
                <span>Catálogo compartido</span>
                <h2 id="catalog-product-editor-title">{editingId ? "Editar producto" : "Agregar producto"}</h2>
              </div>
              <button type="button" className="tablee-tool__icon-button" onClick={cancelForm} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-tool__modal-tabs" role="tablist" aria-label="Secciones del producto">
              <span className={`tablee-tool__modal-tabs-thumb is-${productFormSection}`} />
              <button
                type="button"
                role="tab"
                aria-selected={productFormSection === "main"}
                className={productFormSection === "main" ? "is-active" : ""}
                onClick={() => setProductFormSection("main")}
              >
                Datos
              </button>
              <button type="button" role="tab"
                aria-selected={productFormSection === "highlights"}
                className={productFormSection === "highlights" ? "is-active" : ""}
                onClick={() => setProductFormSection("highlights")}>Destacado</button>
              <button
                type="button"
                role="tab"
                aria-selected={productFormSection === "pricing"}
                className={productFormSection === "pricing" ? "is-active" : ""}
                onClick={() => setProductFormSection("pricing")}
              >
                Precio
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={productFormSection === "images"}
                className={productFormSection === "images" ? "is-active" : ""}
                onClick={() => setProductFormSection("images")}
              >
                Imagenes
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={productFormSection === "description"}
                className={productFormSection === "description" ? "is-active" : ""}
                onClick={() => setProductFormSection("description")}
              >
                Descripcion
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={productFormSection === "options"}
                className={productFormSection === "options" ? "is-active" : ""}
                onClick={() => setProductFormSection("options")}
              >
                Opciones
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={productFormSection === "suggestions"}
                className={productFormSection === "suggestions" ? "is-active" : ""}
                onClick={() => setProductFormSection("suggestions")}
              >
                Sugerencias
              </button>
            </div>

            <div className="tablee-tool__product-panel">
              <div className={`tablee-tool__form-grid is-${productFormSection}`}>
              {productFormSection === "main" ? (
                <>
                  <fieldset className="commerce-catalog__publication tablee-tool__wide-field">
                    <legend>Publicar en tiendas</legend>
                    <p>Puedes seleccionar ambas tiendas. Los pedidos y pagos se gestionan por negocio.</p>
                    <div className="commerce-catalog__stores">{[["tablee", "Tablee", "/images/tablee_logo.png"], ["oliva", "Oliva y Miel", "/ovm_logo.png"]].map(([store, name, logo]) => <label key={store} className={(form.storefronts || []).includes(store) ? "is-selected" : ""}><input type="checkbox" checked={(form.storefronts || []).includes(store)} onChange={(event) => updateField("storefronts", event.target.checked ? [...new Set([...(form.storefronts || []), store])] : (form.storefronts || []).filter((value) => value !== store))} /><img className={`commerce-catalog__store-logo is-${store}`} src={logo} alt="" /><strong>{name}</strong></label>)}</div>
                    {!(form.storefronts || []).length ? <small>Sin tiendas seleccionadas: el producto no aparecerá en las tiendas.</small> : null}
                  </fieldset>

                  {form.isRental ? <div className="commerce-catalog__rental tablee-tool__wide-field">
                    <p>Los arriendos reservan stock por fecha. El precio corresponde a un día y la garantía se cobra por unidad.</p>
                    {form.isRental ? <div className="commerce-catalog__rental-fields">
                      <label className="tablee-tool__field"><span>Garantía reembolsable por unidad (CLP)</span><input type="number" min="0" step="1" value={form.rentalDepositAmount} onChange={(event) => updateField("rentalDepositAmount", event.target.value)} required /></label>
                      <label className="tablee-tool__field tablee-tool__wide-field"><span>Condiciones de uso y devolución</span><textarea rows="3" value={form.rentalTerms} onChange={(event) => updateField("rentalTerms", event.target.value)} placeholder="Estado esperado, limpieza y condiciones del arriendo" /></label>
                      <small>El cliente debe aceptar la garantía al comprar. La devolución se revisa y se autoriza desde la orden.</small>
                    </div> : null}
                  </div> : null}
                  <label className="tablee-tool__field">
                    <span>SKU</span>
                    <input value={form.sku} onChange={(event) => updateField("sku", event.target.value)} />
                  </label>
                  <label className="tablee-tool__field">
                    <span>Nombre</span>
                    <input value={form.name} onChange={(event) => updateField("name", event.target.value)} />
                  </label>
                  <label className="tablee-tool__field">
                    <span>Categoría</span>
                    <div className="tablee-tool__select-action">
                      <CustomSelect
                        value={form.category}
                        options={categorySelectOptions}
                        placeholder="Seleccionar categoria"
                        isOpen={openDropdown === "category"}
                        onOpen={() => setOpenDropdown("category")}
                        onClose={() => setOpenDropdown(null)}
                        onChange={(category) => {
                          updateField("category", category);
                          updateField("subcategory", "");
                        }}
                      />
                      <button type="button" onClick={() => openCategoryModal("category")} aria-label="Agregar categoria">
                        <span className="material-symbols-outlined">add</span>
                      </button>
                    </div>
                  </label>
                  <label className="tablee-tool__field">
                    <span>Subcategoría</span>
                    <div className="tablee-tool__select-action">
                      <CustomSelect
                        value={form.subcategory}
                        options={subcategorySelectOptions}
                        placeholder={form.category ? "Seleccionar subcategoria" : "Selecciona una categoria"}
                        disabled={!form.category}
                        isOpen={openDropdown === "subcategory"}
                        onOpen={() => setOpenDropdown("subcategory")}
                        onClose={() => setOpenDropdown(null)}
                        onChange={(subcategory) => updateField("subcategory", subcategory)}
                      />
                      <button
                        type="button"
                        onClick={() => openCategoryModal("subcategory")}
                        disabled={!form.category}
                        aria-label="Agregar subcategoria"
                      >
                        <span className="material-symbols-outlined">add</span>
                      </button>
                    </div>
                  </label>
                  <label className="tablee-tool__field">
                    <span>Unidad / formato</span>
                    <input value={form.unitLabel} onChange={(event) => updateField("unitLabel", event.target.value)} />
                  </label>
                  <label className="tablee-tool__field">
                    <span>{form.isRental ? "Unidades disponibles para arriendo" : "Stock"}</span>
                    <input type="number" min="0" value={form.stockQty} onChange={(event) => updateField("stockQty", event.target.value)} />
                  </label>

                </>
              ) : productFormSection === "highlights" ? (
                <>
                  <fieldset className="commerce-catalog__home-featured tablee-tool__wide-field">
                    <legend>Tablee</legend>
                    <div className="commerce-catalog__home-featured-controls">
                      <label className="tablee-tool__switch">
                        <input type="checkbox" aria-label="Carrusel Tablee" checked={form.isFeatured} onChange={(event) => updateField("isFeatured", event.target.checked)} />
                        <span />
                        <strong>Destacar en inicio</strong>
                      </label>
                      <label className="tablee-tool__field">
                        <span>Orden</span>
                        <input type="number" min="0" step="1" aria-label="Orden en Tablee" value={form.featuredRank} onChange={(event) => updateField("featuredRank", event.target.value)} disabled={!form.isFeatured} placeholder="Automático" />
                      </label>
                    </div>
                    <p>Menor número primero. Deja vacío para usar el orden automático.</p>
                    <small className={!(form.storefronts || []).includes("tablee") || !form.isActive ? "is-warning" : ""}>{!(form.storefronts || []).includes("tablee") || !form.isActive ? "Para mostrarse, activa el producto y publícalo en Tablee." : "Producto activo y publicado en Tablee."}</small>
                  </fieldset>
                  <fieldset className="commerce-catalog__home-featured tablee-tool__wide-field">
                    <legend>Oliva y Miel</legend>
                    <div className="commerce-catalog__home-featured-controls">
                      <label className="tablee-tool__switch">
                        <input type="checkbox" aria-label="Destacar en inicio de Oliva" checked={form.olivaHomeFeatured} onChange={(event) => updateField("olivaHomeFeatured", event.target.checked)} />
                        <span />
                        <strong>Destacar en inicio</strong>
                      </label>
                      <label className="tablee-tool__field">
                        <span>Orden</span>
                        <input type="number" min="0" step="1" aria-label="Orden en inicio de Oliva" value={form.olivaHomeRank} onChange={(event) => updateField("olivaHomeRank", event.target.value)} disabled={!form.olivaHomeFeatured} placeholder="Automático" />
                      </label>
                    </div>
                    <p>Menor número primero. Deja vacío para usar el orden automático.</p>
                    <small className={!(form.storefronts || []).includes("oliva") || !form.isActive ? "is-warning" : ""}>{!(form.storefronts || []).includes("oliva") || !form.isActive ? "Para mostrarse, activa el producto y publícalo en Oliva y Miel." : "Producto activo y publicado en Oliva y Miel."}</small>
                  </fieldset>
                </>
              ) : productFormSection === "pricing" ? (
                <>
                  <div className="tablee-tool__price-ranges tablee-tool__wide-field">
                    <div className="tablee-tool__price-ranges-head">
                      <span>{form.isRental ? "Tarifa diaria por cantidad" : "Rangos de precio"}</span>
                      <button type="button" onClick={addPriceRange}>
                        <span className="material-symbols-outlined">add</span>
                        Agregar rango
                      </button>
                    </div>
                    <div className="tablee-tool__price-ranges-table">
                      <div className="tablee-tool__price-ranges-row tablee-tool__price-ranges-row--head">
                        <span>Desde</span>
                        <span>Hasta</span>
                        <span>Precio anterior</span>
                        <span>Descuento (%)</span>
                        <span>Precio final</span>
                        <span>Acciones</span>
                      </div>
                      <div className="tablee-tool__price-ranges-body">
                        {normalizePriceRangeLadder(form.priceRanges || []).map((range, index, ranges) => (
                          <div className="tablee-tool__price-ranges-row" key={index}>
                            <label className="tablee-tool__price-field">
                              <span>Desde</span>
                              <input
                                type="number"
                                aria-label={`Desde, rango ${index + 1}`}
                                min="1"
                                value={range.minQty}
                                readOnly
                              />
                            </label>
                            <label className="tablee-tool__price-field">
                              <span>Hasta</span>
                              <input
                                type="number"
                                aria-label={`Hasta, rango ${index + 1}`}
                                min="1"
                                placeholder="Sin limite"
                                value={range.maxQty ?? ""}
                                disabled={index === ranges.length - 1}
                                onChange={(event) => updatePriceRange(index, "maxQty", event.target.value)}
                              />
                            </label>
                            <label className="tablee-tool__price-field">
                              <span>Precio anterior</span>
                              <input
                                type="number"
                                aria-label={`Precio anterior, rango ${index + 1}`}
                                min="0"
                                value={range.previousPrice}
                                onChange={(event) => updatePriceRange(index, "previousPrice", event.target.value)}
                              />
                            </label>
                            <label className="tablee-tool__price-field">
                              <span>Descuento (%)</span>
                              <input
                                type="number"
                                aria-label={`Descuento (%), rango ${index + 1}`}
                                min="0"
                                max="100"
                                placeholder="%"
                                value={range.discountPercent}
                                onChange={(event) => updatePriceRange(index, "discountPercent", normalizeDiscountPercent(event.target.value))}
                              />
                            </label>
                            <label className="tablee-tool__price-field">
                              <span>Precio final</span>
                              <input
                                type="number"
                                aria-label={`Precio final, rango ${index + 1}`}
                                min="0"
                                value={calculateDiscountedPrice(range.previousPrice, range.discountPercent)}
                                readOnly
                              />
                            </label>
                            <div className="tablee-tool__price-actions">
                              <span>Acciones</span>
                              <button
                                type="button"
                                className="tablee-tool__danger"
                                onClick={() => confirmRemovePriceRange(index)}
                                disabled={ranges.length === 1}
                                aria-label="Eliminar rango"
                              >
                                <span className="material-symbols-outlined">delete</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : productFormSection === "images" ? (
                <>
                  <div className="tablee-tool__field tablee-tool__wide-field">
                    <span>Imagen del producto</span>
                    <label className="tablee-tool__upload-button">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={uploadProductImage}
                        disabled={uploadingImage}
                      />
                      <span className="material-symbols-outlined">upload_file</span>
                      <strong>{uploadingImage ? "Subiendo imagen..." : "Subir imagen"}</strong>
                    </label>
                  </div>
                  {form.imageUrls?.length ? (
                    <div className="tablee-tool__uploaded-images tablee-tool__wide-field">
                      <span>Imágenes subidas</span>
                      {form.imageUrls.map((imageUrl) => (
                        <div className="tablee-tool__image-preview" key={imageUrl}>
                          <img src={imageUrl} alt="" />
                          <button
                            type="button"
                            className="tablee-tool__image-remove"
                            onClick={() => removeProductImage(imageUrl)}
                            aria-label="Quitar imagen"
                          >
                            <span className="material-symbols-outlined">close</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : productFormSection === "description" ? (
                <>
                  <div className="tablee-tool__description-tools tablee-tool__wide-field">
                    <button type="button" onClick={addDescriptionBullet}>
                      <span className="material-symbols-outlined">format_list_bulleted</span>
                      Agregar bullet
                    </button>
                  </div>
                  <label className="tablee-tool__field tablee-tool__wide-field">
                    <span>Descripción</span>
                    <textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} />
                  </label>
                </>
              ) : productFormSection === "options" ? (
                <>
                  <div className="tablee-tool__product-options tablee-tool__wide-field">
                    <div className="tablee-tool__price-ranges-head">
                      <div className="tablee-tool__section-title-with-help">
                        <span>Opciones de producto</span>
                        <span className="tablee-tool__help-popover-wrap">
                          <button
                            type="button"
                            className="tablee-tool__help-button"
                            onMouseEnter={(event) => {
                              const rect = event.currentTarget.getBoundingClientRect();
                              setOptionsHelpPosition({
                                top: rect.bottom + 8,
                                left: Math.min(rect.left, window.innerWidth - 388),
                              });
                            }}
                            onMouseLeave={() => setOptionsHelpPosition(null)}
                            aria-label="Ver ayuda sobre opciones de producto"
                            aria-describedby="tablee-product-options-help"
                          >
                            ?
                          </button>
                        </span>
                      </div>
                      <button type="button" onClick={addProductOption}>
                        <span className="material-symbols-outlined">add</span>
                        Agregar opcion
                      </button>
                    </div>
                    <div className="tablee-tool__product-options-table">
                      <div className="tablee-tool__product-options-row tablee-tool__product-options-row--head">
                        <span>Opcion</span>
                        <span>Valores</span>
                        <span>Acciones</span>
                      </div>
                      <div className="tablee-tool__product-options-body">
                        {(form.productOptions || []).length ? (form.productOptions || []).map((option, index) => (
                          <div className="tablee-tool__product-options-row" key={index}>
                            <label className="tablee-tool__price-field">
                              <span>Opcion</span>
                              <input
                                aria-label={`Nombre de opción, grupo ${index + 1}`}
                                placeholder="Sabor"
                                value={option.name || ""}
                                onChange={(event) => updateProductOption(index, "name", event.target.value)}
                              />
                            </label>
                            <label className="tablee-tool__price-field">
                              <span>Valores</span>
                              <input
                                aria-label={`Valores de opción, grupo ${index + 1}`}
                                placeholder="Frutilla, Chocolate, Vainilla"
                                value={(option.values || []).join(", ")}
                                onChange={(event) => updateProductOption(index, "values", event.target.value)}
                              />
                            </label>
                            <div className="tablee-tool__price-actions">
                              <span>Acciones</span>
                              <button
                                type="button"
                                className="tablee-tool__danger"
                                onClick={() => removeProductOption(index)}
                                aria-label="Eliminar opcion"
                              >
                                <span className="material-symbols-outlined">delete</span>
                              </button>
                            </div>
                          </div>
                        )) : (
                          <p className="tablee-tool__empty-options">Agrega opciones como sabor, tamaño o formato para que el cliente seleccione en el sitio.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="tablee-tool__product-suggestions tablee-tool__wide-field">
                    <div className="tablee-tool__price-ranges-head">
                      <span>Productos sugeridos</span>
                    </div>
                    <div className="tablee-tool__suggestion-picker">
                      <ProductSuggestionSelect
                        value={selectedSuggestionProductId}
                        products={suggestionProductOptions}
                        query={suggestionQuery}
                        placeholder="Seleccionar producto"
                        disabled={!suggestionProductOptions.length}
                        isOpen={openDropdown === "suggestedProduct"}
                        onOpen={() => setOpenDropdown("suggestedProduct")}
                        onClose={() => setOpenDropdown(null)}
                        onQueryChange={setSuggestionQuery}
                        onChange={(productId) => setSelectedSuggestionProductId(productId)}
                      />
                      <button
                        type="button"
                        onClick={addProductSuggestion}
                        disabled={!selectedSuggestionProductId}
                        aria-label="Agregar producto sugerido"
                      >
                        <span className="material-symbols-outlined">add</span>
                      </button>
                    </div>
                    <div className="tablee-tool__product-options-table tablee-tool__suggestions-table">
                      <div className="tablee-tool__suggestions-row tablee-tool__suggestions-row--head">
                        <span>Producto</span>
                        <span>Tipo</span>
                        <span>Prioridad</span>
                        <span>Activo</span>
                        <span>Acciones</span>
                      </div>
                      <div className="tablee-tool__product-options-body tablee-tool__suggestions-body">
                        {(form.suggestions || []).length ? (form.suggestions || []).map((suggestion, index) => {
                          const suggestedProduct = productById[String(suggestion.suggestedProductId)];
                          return (
                            <div className="tablee-tool__suggestions-row" key={`${suggestion.suggestedProductId}-${index}`}>
                              <div className="tablee-tool__suggested-product">
                                <img src={suggestedProduct?.imageUrl || "/images/tablee_logo.png"} alt="" />
                                <div>
                                  <strong>{suggestedProduct?.name || "Producto no encontrado"}</strong>
                                  <small>{[suggestedProduct?.sku, suggestedProduct?.category].filter(Boolean).join(" · ")}</small>
                                </div>
                              </div>
                              <CustomSelect
                                value={suggestion.type || "complement"}
                                options={suggestionTypeOptions}
                                placeholder="Tipo"
                                isOpen={openDropdown === `suggestionType-${index}`}
                                onOpen={() => setOpenDropdown(`suggestionType-${index}`)}
                                onClose={() => setOpenDropdown(null)}
                                onChange={(value) => updateProductSuggestion(index, "type", value)}
                              />
                              <label className="tablee-tool__price-field">
                                <span>Prioridad</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={suggestion.priority || index + 1}
                                  onChange={(event) => updateProductSuggestion(index, "priority", event.target.value)}
                                />
                              </label>
                              <label className="tablee-tool__switch tablee-tool__suggestion-switch">
                                <input
                                  type="checkbox"
                                  checked={suggestion.isActive !== false}
                                  onChange={(event) => updateProductSuggestion(index, "isActive", event.target.checked)}
                                />
                                <span />
                                <strong>{suggestion.isActive !== false ? "Si" : "No"}</strong>
                              </label>
                              <div className="tablee-tool__price-actions">
                                <span>Acciones</span>
                                <button
                                  type="button"
                                  className="tablee-tool__danger"
                                  onClick={() => removeProductSuggestion(index)}
                                  aria-label="Eliminar sugerencia"
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              </div>
                            </div>
                          );
                        }) : (
                          <p className="tablee-tool__empty-options">Agrega productos que se sugeriran cuando este producto este en el carro.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
              </div>
            </div>

            <div className="tablee-tool__modal-actions">
              <div className="tablee-tool__modal-status">
                <label className="tablee-tool__footer-switch">
                  <input type="checkbox" checked={form.isActive} onChange={(event) => updateField("isActive", event.target.checked)} />
                  <span />
                  <strong>Publicado</strong>
                </label>
                <label className="tablee-tool__footer-switch">
                  <input type="checkbox" aria-label="Este producto es un arriendo" checked={form.isRental} onChange={(event) => updateField("isRental", event.target.checked)} />
                  <span />
                  <strong>Arriendo</strong>
                </label>
                {!form.isRental ? <label className="tablee-tool__footer-switch" title="Activado: cada pedido descuenta stock y las cancelaciones lo reponen. Desactivado: producto hecho a pedido, sin límite de stock.">
                  <input type="checkbox" aria-label="Controlar stock en la tienda online" checked={Boolean(form.trackStock)} onChange={(event) => updateField("trackStock", event.target.checked)} />
                  <span />
                  <strong>Controlar stock en tienda</strong>
                </label> : null}
                {message ? <p>{message}</p> : null}
              </div>
              <button type="button" className="tablee-tool__secondary" onClick={cancelForm}>Cancelar</button>
              <button type="submit" disabled={uploadingImage}>{editingId ? "Guardar" : "Agregar"}</button>
            </div>
          </form>
        </div>
      ) : null}
      {showCategoryModal ? (
        <div className="tablee-tool__inner-modal-backdrop" role="presentation" onMouseDown={closeCategoryModal}>
          <div
            className={`tablee-tool__inner-modal${categoryModalMode === "manage" ? " is-manage" : ""}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-tool__modal-head">
              <div>
                <span>Catalogo</span>
                <h2>{categoryModalMode === "manage" ? "Administrar categorias" : "Agregar categoria"}</h2>
              </div>
              <button type="button" className="tablee-tool__icon-button" onClick={closeCategoryModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            {categoryModalMode === "manage" ? (
              <>
                <div className="tablee-tool__category-manager-toolbar">
                  <button
                    type="button"
                    className="tablee-tool__category-manager-add"
                    onClick={createEditableCategoryEntry}
                    disabled={creatingCategoryEntry}
                    aria-label="Agregar categoria"
                  >
                    {creatingCategoryEntry ? "Agregando..." : "Agregar categoria"}
                  </button>
                </div>
                <div className="tablee-tool__category-manager">
                  <div className="tablee-tool__category-manager-list">
                    {categoryTree.length ? categoryTree.map((categoryGroup) => (
                      <div className="tablee-tool__category-tree-row" key={categoryGroup.category}>
                        <div className="tablee-tool__category-tree-parent">
                          <button
                            type="button"
                            className={`tablee-tool__category-tree-collapse${collapsedCategories[categoryGroup.category] ? " is-collapsed" : ""}`}
                            onClick={() => toggleCategoryCollapsed(categoryGroup.category)}
                            aria-label={`${collapsedCategories[categoryGroup.category] ? "Expandir" : "Colapsar"} ${categoryGroup.category}`}
                            aria-expanded={!collapsedCategories[categoryGroup.category]}
                          >
                            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                              <path d="M7.2 4.6 12.6 10l-5.4 5.4" />
                            </svg>
                          </button>
                          {editingCategory?.category === categoryGroup.category ? (
                            <input
                              className="tablee-tool__category-tree-edit-input"
                              value={editingCategory.value}
                              autoFocus
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  saveCategoryGroupEdit();
                                }
                                if (event.key === "Escape") {
                                  setEditingCategory(null);
                                }
                              }}
                              onChange={(event) => setEditingCategory((current) => ({ ...current, value: event.target.value }))}
                            />
                          ) : (
                            <strong>{categoryGroup.category}</strong>
                          )}
                          <div className="tablee-tool__category-tree-actions">
                            {editingCategory?.category === categoryGroup.category ? (
                              <>
                                <button
                                  type="button"
                                  className="tablee-tool__category-tree-icon"
                                  onClick={saveCategoryGroupEdit}
                                  aria-label={`Guardar ${categoryGroup.category}`}
                                >
                                  <span className="material-symbols-outlined">check</span>
                                </button>
                                <button
                                  type="button"
                                  className="tablee-tool__category-tree-icon"
                                  onClick={() => setEditingCategory(null)}
                                  aria-label="Cancelar edicion"
                                >
                                  <span className="material-symbols-outlined">close</span>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="tablee-tool__category-tree-plus"
                                  onClick={() => handleCategoryPlusClick(categoryGroup.category)}
                                  aria-label={`Agregar subcategoria a ${categoryGroup.category}`}
                                >
                                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                    <path d="M10 4v12M4 10h12" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="tablee-tool__category-tree-icon"
                                  onClick={() => startEditCategoryGroup(categoryGroup.category)}
                                  aria-label={`Editar ${categoryGroup.category}`}
                                >
                                  <span className="material-symbols-outlined">edit</span>
                                </button>
                                <button
                                  type="button"
                                  className="tablee-tool__category-tree-icon tablee-tool__danger"
                                  onClick={() => deleteCategoryGroup(categoryGroup.category)}
                                  aria-label={`Eliminar ${categoryGroup.category}`}
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        {!collapsedCategories[categoryGroup.category] ? (
                          <div className="tablee-tool__category-tree-children">
                            {categoryGroup.options.map((category) => (
                              <div className="tablee-tool__category-tree-child" key={category.id || `${category.category}-${category.subcategory}`}>
                                <span className="material-symbols-outlined">subdirectory_arrow_right</span>
                                {editingSubcategory?.id === category.id ? (
                                  <input
                                    className="tablee-tool__category-tree-edit-input"
                                    value={editingSubcategory.value}
                                    autoFocus
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        saveSubcategoryOptionEdit();
                                      }
                                      if (event.key === "Escape") {
                                        setEditingSubcategory(null);
                                      }
                                    }}
                                    onChange={(event) => setEditingSubcategory((current) => ({ ...current, value: event.target.value }))}
                                  />
                                ) : (
                                  <strong>{category.subcategory || "General"}</strong>
                                )}
                                <div className="tablee-tool__category-tree-actions">
                                  {editingSubcategory?.id === category.id ? (
                                    <>
                                      <button
                                        type="button"
                                        className="tablee-tool__category-tree-icon"
                                        onClick={saveSubcategoryOptionEdit}
                                        aria-label={`Guardar ${category.category} ${category.subcategory || ""}`}
                                      >
                                        <span className="material-symbols-outlined">check</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="tablee-tool__category-tree-icon"
                                        onClick={() => setEditingSubcategory(null)}
                                        aria-label="Cancelar edicion"
                                      >
                                        <span className="material-symbols-outlined">close</span>
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="tablee-tool__category-tree-icon"
                                        onClick={() => startEditSubcategoryOption(category)}
                                        aria-label={`Editar ${category.category} ${category.subcategory || ""}`}
                                      >
                                        <span className="material-symbols-outlined">edit</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="tablee-tool__category-tree-icon tablee-tool__danger"
                                        onClick={() => deleteCategoryOption(category)}
                                        aria-label={`Eliminar ${category.category} ${category.subcategory || ""}`}
                                      >
                                        <span className="material-symbols-outlined">delete</span>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                            {addingSubcategoryFor[categoryGroup.category] ? (
                              <div className="tablee-tool__category-tree-add">
                                <span className="material-symbols-outlined">subdirectory_arrow_right</span>
                                <input
                                  value={subcategoryDrafts[categoryGroup.category] || ""}
                                  placeholder="Nueva subcategoria"
                                  autoFocus
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      addSubcategoryToCategory(categoryGroup.category);
                                    }
                                    if (event.key === "Escape") {
                                      setAddingSubcategoryFor((current) => ({ ...current, [categoryGroup.category]: false }));
                                      setSubcategoryDrafts((current) => ({ ...current, [categoryGroup.category]: "" }));
                                    }
                                  }}
                                  onChange={(event) =>
                                    setSubcategoryDrafts((current) => ({
                                      ...current,
                                      [categoryGroup.category]: event.target.value,
                                    }))
                                  }
                                />
                                <div className="tablee-tool__category-tree-actions">
                                  <button
                                    type="button"
                                    className="tablee-tool__category-tree-icon"
                                    onClick={() => addSubcategoryToCategory(categoryGroup.category)}
                                    aria-label={`Guardar subcategoria en ${categoryGroup.category}`}
                                  >
                                    <span className="material-symbols-outlined">check</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="tablee-tool__category-tree-icon"
                                    onClick={() => {
                                      setAddingSubcategoryFor((current) => ({ ...current, [categoryGroup.category]: false }));
                                      setSubcategoryDrafts((current) => ({ ...current, [categoryGroup.category]: "" }));
                                    }}
                                    aria-label="Cancelar subcategoria"
                                  >
                                    <span className="material-symbols-outlined">close</span>
                                  </button>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )) : (
                      <div className="tablee-tool__category-tree-empty">
                        Agrega la primera categoria para comenzar.
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
            {categoryModalMode === "manage" ? null : (
              <div className="tablee-tool__form-grid tablee-tool__inner-form-grid">
                <label className="tablee-tool__field tablee-tool__wide-field">
                  <span>Categoría</span>
                  <input
                    value={categoryForm.category}
                    onChange={(event) => setCategoryForm((current) => ({ ...current, category: event.target.value }))}
                  />
                </label>
                <label className="tablee-tool__field tablee-tool__wide-field">
                  <span>Subcategoría</span>
                  <input
                    value={categoryForm.subcategory}
                    onChange={(event) => setCategoryForm((current) => ({ ...current, subcategory: event.target.value }))}
                  />
                </label>
              </div>
            )}
            <div className="tablee-tool__modal-actions">
              <div className="tablee-tool__modal-status">
                {message ? <p>{message}</p> : null}
              </div>
              <button type="button" className="tablee-tool__secondary" onClick={closeCategoryModal}>Cancelar</button>
              <button type="button" onClick={categoryModalMode === "manage" ? saveCategoryManagerChanges : saveCategoryOption}>
                {categoryModalMode === "manage" ? "Guardar" : "Agregar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {optionsHelpPosition ? createPortal(
        <div
          id="tablee-product-options-help"
          className="tablee-tool__options-help-popover"
          role="tooltip"
          style={{
            top: `${optionsHelpPosition.top}px`,
            left: `${Math.max(16, optionsHelpPosition.left)}px`,
          }}
        >
          <span className="tablee-tool__options-help-popover-title">Como funcionan</span>
          <span>Usa esta seccion cuando un producto necesita una seleccion del cliente, por ejemplo sabor, tamaño, formato o estilo.</span>
          <span><strong>Opcion:</strong> es el nombre de lo que el cliente debe elegir. Ejemplos: Sabor, Tamaño, Formato.</span>
          <span><strong>Valores:</strong> son las alternativas disponibles, separadas por coma. Ejemplo: Chocolate, Vainilla, Frutilla.</span>
          <span><strong>En la orden:</strong> las selecciones quedan guardadas y aparecen en la business app.</span>
        </div>,
        document.body
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export default TableeOrders;
